import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import { apiClient } from "../utils/apiClient";

interface ClosingHistory {
  id: number;
  event: string;
  worked_on_limit: string | null;
  updated_at: string;
  user_name: string;
}

interface ClosingInfo {
  worked_on_limit: string | null;
  updated_at: string | null;
  user_name: string | null;
  history?: ClosingHistory[];
}

const ClosingMaintenance = () => {
  const [closingInfo, setClosingInfo] = useState<ClosingInfo | null>(null);
  const [newLimitDate, setNewLimitDate] = useState("");
  const [loading, setLoading] = useState(false);

  // メッセージ表示用ステート（他画面と統一）
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchClosingStatus();
  }, []);

  const fetchClosingStatus = async () => {
    try {
      const data = await apiClient.get<ClosingInfo>("/api/closing_statuses");
      setClosingInfo(data);
      if (data.worked_on_limit) {
        setNewLimitDate(data.worked_on_limit);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setErrorMessage("現在の設定の取得に失敗しました");
    }
  };

  const resetMessages = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (!newLimitDate) {
      setErrorMessage("新しい締め日を指定してください");
      return;
    }

    if (
      !window.confirm(
        `${newLimitDate} 以前のデータを編集不可にします。\nよろしいですか？`,
      )
    )
      return;

    setLoading(true);
    resetMessages();
    try {
      await apiClient.post("/api/closing_statuses", {
        worked_on_limit: newLimitDate,
      });
      setSuccessMessage("締め日を更新しました");
      fetchClosingStatus();
    } catch (err: any) {
      setErrorMessage(err.message || "更新に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}
    >
      <Header title="締め日管理" />

      <div className="limit-width px-4">
        {/* 戻るリンク */}
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-decoration-none text-secondary d-inline-flex align-items-center"
          >
            <span className="fs-4 me-2">‹</span> 管理者メニューに戻る
          </Link>
        </div>

        {/* メッセージエリア */}
        {successMessage && (
          <div className="alert alert-success mb-4 border-0 shadow-sm">
            ✅ {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-danger mb-4 border-0 shadow-sm">
            ⚠️ {errorMessage}
          </div>
        )}

        {/* 設定変更フォーム */}
        <div className="card mb-4 shadow-sm border-0 bg-light">
          <div className="card-body">
            <div className="d-flex align-items-end gap-3">
              <div style={{ flex: 1, maxWidth: "300px" }}>
                <label className="form-label small fw-bold">新しい締め日</label>
                <input
                  type="date"
                  className="form-control"
                  value={newLimitDate}
                  onChange={(e) => setNewLimitDate(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary fw-bold px-4"
                onClick={handleSave}
                disabled={loading || !newLimitDate}
              >
                {loading ? "更新中..." : "適用"}
              </button>
            </div>
            <div className="mt-3 text-danger small fw-bold">
              ※指定した日付（当日を含む）以前の業務報告は、管理者を含めすべてのユーザーが編集・削除できなくなります。
            </div>
          </div>
        </div>

        {/* 現在の適用状況 */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white border-bottom-0 pt-4 px-4 pb-0">
            <h5 className="fw-bold mb-0">現在の適用状況</h5>
          </div>
          <div className="card-body p-4">
            {closingInfo?.worked_on_limit ? (
              <div className="d-flex align-items-center bg-white border rounded p-4 shadow-sm">
                <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3 me-4">
                  {/* Bootstrap Icons: Calendar Check */}
                  <svg
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                  </svg>
                </div>
                <div>
                  <div className="d-flex align-items-baseline mb-1">
                    <span className="display-6 fw-bold text-dark me-3">
                      {closingInfo.worked_on_limit}
                    </span>
                    <span className="text-secondary fw-bold">
                      まで締め切り済み
                    </span>
                  </div>
                  <div className="text-muted small">
                    最終更新:{" "}
                    {new Date(closingInfo.updated_at!).toLocaleString()} （担当:{" "}
                    {closingInfo.user_name}）
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted bg-light rounded border">
                <p className="mb-0">現在、締め日は設定されていません。</p>
                <small>すべての期間のデータが編集可能な状態です。</small>
              </div>
            )}
          </div>
        </div>

        {/* 変更履歴（PaperTrailから取得） */}
        {closingInfo?.history && closingInfo.history.length > 0 && (
          <div className="card shadow-sm border-0 mb-5">
            <div className="card-header bg-white border-bottom-0 pt-4 px-4 pb-0">
              <h5 className="fw-bold mb-0">変更履歴</h5>
            </div>
            <div className="card-body p-4">
              <div className="table-responsive border rounded">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "25%" }} className="ps-4">
                        変更日時
                      </th>
                      <th style={{ width: "20%" }}>操作</th>
                      <th style={{ width: "30%" }}>設定された締め日</th>
                      <th style={{ width: "25%" }}>操作者</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closingInfo.history.map((hist) => (
                      <tr key={hist.id}>
                        <td className="ps-4 text-secondary">
                          {new Date(hist.updated_at).toLocaleString()}
                        </td>
                        <td>
                          <span
                            className={`badge ${hist.event === "create" ? "bg-primary" : "bg-info"}`}
                          >
                            {hist.event === "create" ? "初回設定" : "更新"}
                          </span>
                        </td>
                        <td className="fw-bold text-dark">
                          {hist.worked_on_limit || "未設定"}
                        </td>
                        <td className="text-secondary">{hist.user_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClosingMaintenance;
